import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'antd';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { toast } from 'react-hot-toast';

import { QuoteDetailDropdown } from '../components/quotes/QuoteDetailDropdown';
import {
  ConvertedModal,
  ExpiredModal,
  FollowUpModal,
  LiveModal,
  LostModal,
  ProjectModal,
} from '../components/quotes/status-modals';
import { CompleteFollowUpModal } from '@/features/dashboard/components/CompleteFollowUpModal';
import { getQuoteFollowupsByQuoteId } from '@/features/dashboard/api';
import { FOLLOWUP_TYPE_MAP } from '@/features/dashboard/constants';
import { useCreateQuoteFollowup, useUpdateQuoteFollowup } from '@/features/dashboard/hooks';
import {
  useCloseQuote,
  useConvertQuoteToOrder,
  useUpdateQuote,
  useQuoteDetailFromDataverse,
  useCreateNote,
} from '../hooks';
import { useGetCurrentUser } from '@/hooks/app';
import { QuoteStateCode, QuoteStatus } from '../types';
import { PermissionChecker } from '@/utils/permission';
import { PaymentStatus } from '@/features/orders-management/constants';
import { createPaymentApi, updateOrderApi } from '@/features/orders-management/api/dataverse';
import { updateQuote } from '../api/dataverse';

const QuoteDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: currentUserData } = useGetCurrentUser();
  const permissions = currentUserData?.permission;
  const permissionChecker = new PermissionChecker(permissions);
  const canConvertQuoteToOrder = permissionChecker.canConvertQuoteToOrder();
  const canManageQuotesStatus = permissionChecker.canManageQuotesStatus();
  const canManageFollowup = permissionChecker.canManageFollowup();

  const [liveModalOpen, setLiveModalOpen] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [convertedModalOpen, setConvertedModalOpen] = useState(false);
  const [lostModalOpen, setLostModalOpen] = useState(false);
  const [expiredModalOpen, setExpiredModalOpen] = useState(false);
  const [completeFollowUpModalOpen, setCompleteFollowUpModalOpen] = useState(false);
  const [isCompletingFollowup, setIsCompletingFollowup] = useState(false);

  const updateQuoteMutation = useUpdateQuote();
  const createNoteMutation = useCreateNote();
  const closeQuoteMutation = useCloseQuote();
  const convertToOrderMutation = useConvertQuoteToOrder();
  const createFollowupMutation = useCreateQuoteFollowup();
  const updateFollowupMutation = useUpdateQuoteFollowup();

  const quoteId = id as string;
  const { data: quoteDetail } = useQuoteDetailFromDataverse(quoteId);
  const showPaymentDestination = quoteDetail?.customerid_account?.paymenttermscode === 1;

  const handleStatusSelect = (status: QuoteStatus) => {
    switch (status) {
      case QuoteStatus.LIVE:
        setLiveModalOpen(true);
        break;
      case QuoteStatus.FOLLOW_UP_NEEDED:
        setFollowUpModalOpen(true);
        break;
      case QuoteStatus.CONVERTED:
        setConvertedModalOpen(true);
        break;
      case QuoteStatus.LOST:
        setLostModalOpen(true);
        break;
      case QuoteStatus.EXPIRED:
        setExpiredModalOpen(true);
        break;
    }
  };

  const handleLiveConfirm = () => {
    updateQuoteMutation.mutate(
      { id: quoteId, payload: { statuscode: QuoteStatus.LIVE, statecode: QuoteStateCode.ACTIVE } },
      { onSuccess: () => setLiveModalOpen(false) }
    );
  };

  const handleFollowUpConfirm = (formData: any) => {
    const dogmaType = FOLLOWUP_TYPE_MAP[formData.followUpType] || 1;
    createFollowupMutation.mutate(
      {
        dogma_name: `Follow Up - ${formData.followUpType}`,
        'dogma_Quote@odata.bind': `/quotes(${quoteId})`,
        dogma_notes: formData.remarks || '',
        dogma_type: dogmaType,
        dogma_followupdate: formData.followUpDate?.format('YYYY-MM-DD') || '',
      },
      { onSuccess: () => setFollowUpModalOpen(false) }
    );
    updateQuoteMutation.mutate({
      id: quoteId,
      payload: { statuscode: QuoteStatus.FOLLOW_UP_NEEDED, statecode: QuoteStateCode.DRAFT, description: '' },
    });
  };

  const handleProjectConfirm = (remarks?: string) => {
    updateQuoteMutation.mutate(
      { id: quoteId, payload: { statuscode: QuoteStatus.FOLLOW_UP_NEEDED, statecode: QuoteStateCode.DRAFT, description: remarks } },
      { onSuccess: () => setProjectModalOpen(false) }
    );
  };

  const handleConvertedConfirm = async (formData: { remarks?: string; destination: 'email' | 'sms'; requestdeliveryby: string }) => {
    try {
      const paymentTermsCode = quoteDetail?.customerid_account?.paymenttermscode;

      // Step 1: Patch quote to Live
      await updateQuote({ id: quoteId, payload: { statuscode: QuoteStatus.LIVE, statecode: QuoteStateCode.ACTIVE } });

      // Step 2: Create order
      const orderResult = await convertToOrderMutation.mutateAsync({
        quoteid: quoteId,
        dogma_paymentstatus: paymentTermsCode === 1 ? PaymentStatus.PAYMENT_LINK_SENT : PaymentStatus.CREDIT,
        description: formData.remarks,
        requestdeliveryby: formData.requestdeliveryby,
      });

      // Step 3: Patch quote to Converted
      await updateQuote({ id: quoteId, payload: { statuscode: QuoteStatus.CONVERTED } });

      // Step 4: If payment link account, create payment and set order to Awaiting Payment
      if (paymentTermsCode === 1 && orderResult?.orderId) {
        const exchangeRate = (orderResult as any)?.orderDetails?.dogma_exchangerate ?? quoteDetail?.dogma_exchangerate ?? 1;
        const vssTotal = (orderResult as any)?.orderDetails?.dogma_vsstotalamount ?? 0;
        await createPaymentApi({
          total: Math.round(vssTotal * exchangeRate * 100),
          currency: quoteDetail?.dogma_DealersCurrency?.dogma_isocurrencycode ?? 'GBP',
          userPaymentId: `${(orderResult as any)?.dogma_ordernumber ?? (orderResult as any)?.ordernumber ?? ''}-${quoteDetail?.customerid_account?.name ?? ''}`,
          email: quoteDetail?.customerid_contact?.emailaddress1 ?? '',
          phone: quoteDetail?.customerid_contact?.telephone1 ?? '',
          orderid: orderResult.orderId,
          destination: formData.destination,
        });
        await updateOrderApi({ id: orderResult.orderId, statuscode: 736000003 });
      }

      setConvertedModalOpen(false);
    } catch {
      // mutations handle their own error toasts
    }
  };

  const handleLostConfirm = (remarks?: string) => {
    closeQuoteMutation.mutate(
      { quoteid: quoteId, subject: remarks || 'Quote Lost', Status: QuoteStatus.LOST },
      { onSuccess: () => setLostModalOpen(false) }
    );
  };

  const handleExpiredConfirm = (remarks?: string) => {
    closeQuoteMutation.mutate(
      { quoteid: quoteId, subject: remarks || 'Quote Expired', Status: QuoteStatus.EXPIRED },
      { onSuccess: () => setExpiredModalOpen(false) }
    );
  };

  const handleCompleteFollowUpConfirm = async () => {
    setIsCompletingFollowup(true);
    try {
      const followupsResponse = await getQuoteFollowupsByQuoteId(quoteId);
      if (!followupsResponse?.value?.length) {
        toast.error('No active follow-ups found for this quote');
        return;
      }
      const followupId = followupsResponse.value[0]?.dogma_quotefollowupid;
      await updateFollowupMutation.mutateAsync({ id: followupId as string, statecode: 1, statuscode: 2 });
      await updateQuoteMutation.mutateAsync({
        id: quoteId,
        payload: { statuscode: QuoteStatus.LIVE, statecode: QuoteStateCode.ACTIVE },
      });
      toast.success('Follow-up marked as complete successfully');
      setCompleteFollowUpModalOpen(false);
    } catch {
      toast.error('Failed to mark follow-up as complete');
    } finally {
      setIsCompletingFollowup(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 w-full">
      <Button
        type="text"
        icon={<IoMdArrowRoundBack />}
        onClick={() => navigate(-1)}
        className="mb-4"
      >
        Back to Quotes
      </Button>

      <QuoteDetailDropdown
        quoteId={quoteId}
        isExpanded={true}
        onToggle={() => {}}
        onStatusChange={() => {}}
        isShowHeader={true}
        onStatusSelect={handleStatusSelect}
        onFollowUp={() => setFollowUpModalOpen(true)}
        onCompleteFollowUp={() => setCompleteFollowUpModalOpen(true)}
        onConvertToOrder={() => setConvertedModalOpen(true)}
        onAddNote={(note) =>
          createNoteMutation.mutate({
            entityType: 'quote',
            recordId: quoteId,
            subject: 'Customer Note',
            noteText: note,
          })
        }
        canConvertQuoteToOrder={canConvertQuoteToOrder}
        canManageQuotesStatus={canManageQuotesStatus}
        canManageFollowup={canManageFollowup}
      />

      <LiveModal
        open={liveModalOpen}
        onClose={() => setLiveModalOpen(false)}
        onConfirm={handleLiveConfirm}
        isLoading={updateQuoteMutation.isPending}
      />

      <FollowUpModal
        open={followUpModalOpen}
        onClose={() => setFollowUpModalOpen(false)}
        onConfirm={handleFollowUpConfirm}
        isLoading={createFollowupMutation.isPending}
      />

      <ProjectModal
        open={projectModalOpen}
        onClose={() => setProjectModalOpen(false)}
        onConfirm={handleProjectConfirm}
        isLoading={updateQuoteMutation.isPending}
      />

      <ConvertedModal
        open={convertedModalOpen}
        onClose={() => setConvertedModalOpen(false)}
        onConfirm={handleConvertedConfirm}
        isLoading={convertToOrderMutation.isPending}
        showPaymentDestination={showPaymentDestination}
        quoteId={quoteId}
        region={quoteDetail?.dogma_region ?? null}
      />

      <LostModal
        open={lostModalOpen}
        onClose={() => setLostModalOpen(false)}
        onConfirm={handleLostConfirm}
        isLoading={closeQuoteMutation.isPending}
      />

      <ExpiredModal
        open={expiredModalOpen}
        onClose={() => setExpiredModalOpen(false)}
        onConfirm={handleExpiredConfirm}
        isLoading={closeQuoteMutation.isPending}
      />

      <CompleteFollowUpModal
        open={completeFollowUpModalOpen}
        onClose={() => setCompleteFollowUpModalOpen(false)}
        onConfirm={handleCompleteFollowUpConfirm}
        isLoading={isCompletingFollowup}
      />
    </div>
  );
};

export default QuoteDetailPage;
